import { useCallback, useRef, useState } from "react";
import { Exterior360Service } from "@/services/exterior360/exterior360Service";
import { compressImageForFullSize } from "@/utils/imageCompression";
import type { ViewType360 } from "@/types/exterior360";

export type UploadItemStatus = "pending" | "compressing" | "uploading" | "done" | "failed";

export interface UploadQueueItem {
  id: string;
  frameIndex: number;
  viewType: ViewType360;
  blob: Blob;
  status: UploadItemStatus;
  error?: string;
  /** Server id when done */
  resultId?: string;
  /** Server preview URL when done */
  resultUrl?: string;
  /** Server full-size URL when done (optional) */
  resultUrlFull?: string;
}

const MAX_CONCURRENT = 4;

export function useExterior360UploadQueue(carTestId: string | null) {
  const [queue, setQueue] = useState<UploadQueueItem[]>([]);
  const processingRef = useRef(false);
  const queueRef = useRef(queue);
  queueRef.current = queue;

  const processQueue = useCallback(async () => {
    if (!carTestId || processingRef.current) return;

    const q = queueRef.current;
    const pending = q.filter((item) => item.status === "pending");
    const inProgress = q.filter((item) => item.status === "compressing" || item.status === "uploading");
    if (pending.length === 0 || inProgress.length >= MAX_CONCURRENT) return;

    processingRef.current = true;

    const next = pending[0];
    setQueue((prev) =>
      prev.map((q) => (q.id === next.id ? { ...q, status: "compressing" as const } : q))
    );

    try {
      const ext = next.blob.type.includes("png") ? "png" : "jpg";
      let file = new File([next.blob], `360_${next.frameIndex}.${ext}`, {
        type: next.blob.type,
      });

      try {
        file = await compressImageForFullSize(file);
      } catch {
        // Keep original if compression fails
      }

      setQueue((prev) =>
        prev.map((q) => (q.id === next.id ? { ...q, status: "uploading" as const } : q))
      );

      const result = await Exterior360Service.uploadFrame(
        carTestId,
        next.frameIndex,
        next.viewType,
        file
      );

      setQueue((prev) =>
        prev.map((q) =>
          q.id === next.id
            ? {
                ...q,
                status: "done" as const,
                resultId: result.id,
                resultUrl: result.url,
                resultUrlFull: (result as { urlFull?: string }).urlFull,
              }
            : q
        )
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setQueue((prev) =>
        prev.map((q) =>
          q.id === next.id ? { ...q, status: "failed" as const, error: msg } : q
        )
      );
    } finally {
      processingRef.current = false;
      setTimeout(() => processQueue(), 0);
    }
  }, [carTestId]);

  const triggerProcess = useCallback(() => {
    setTimeout(() => processQueue(), 50);
  }, [processQueue]);

  const enqueue = useCallback((frameIndex: number, viewType: ViewType360, blob: Blob) => {
    const id = `360-${frameIndex}-${Date.now()}`;
    const item: UploadQueueItem = {
      id,
      frameIndex,
      viewType,
      blob,
      status: "pending",
    };
    setQueue((prev) => [...prev, item]);
    triggerProcess();
  }, [triggerProcess]);

  const retry = useCallback((id: string) => {
    setQueue((prev) =>
      prev.map((q) => (q.id === id ? { ...q, status: "pending" as const, error: undefined } : q))
    );
    triggerProcess();
  }, [triggerProcess]);

  const doneCount = queue.filter((q) => q.status === "done").length;
  const failedCount = queue.filter((q) => q.status === "failed").length;
  const isComplete = queue.length > 0 && queue.every((q) => q.status === "done" || q.status === "failed");
  const allDone = queue.length > 0 && queue.every((q) => q.status === "done");

  return {
    queue,
    enqueue,
    retry,
    doneCount,
    failedCount,
    isComplete,
    allDone,
    total: queue.length,
  };
}
